import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createQueryHooks } from '../lib/create-query-hooks.ts';
import * as endpoints from '../api/endpoints/servers.ts';
import type {
  CreateServerParams,
  CreateServerResult,
  UpdateServerParams,
  CreateServerGroupParams,
  UpdateServerGroupParams,
  ExecuteCommandParams,
  TestConnectionRawParams,
  AttachSSHParams,
} from '../api/types.ts';
import { STALE_TIMES, GC_TIMES } from '../lib/constants.ts';

const {
  queryKeys: _serverQueryKeys,
  useList: useServers,
  useDetail: useServer,
  useDelete: useDeleteServer,
} = createQueryHooks({
  resourceKey: 'servers',
  endpoints: {
    list: endpoints.listServers,
    getById: endpoints.getServer,
    delete: endpoints.deleteServer,
  },
});

export function useCreateServer() {
  const queryClient = useQueryClient();
  return useMutation<CreateServerResult, Error, CreateServerParams>({
    mutationFn: (params: CreateServerParams) => endpoints.createServer(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: _serverQueryKeys.all });
    },
  });
}

// Extended query keys (superset of factory's queryKeys)
export const serverQueryKeys = {
  ..._serverQueryKeys,
  groups: ['server-groups'] as const,
  groupList: (teamId?: string) => ['server-groups', 'list', teamId] as const,
  handoff: (serverId: string) => ['servers', 'handoff', serverId] as const,
  runtimeInventory: (serverId: string) => ['servers', 'runtime-inventory', serverId] as const,
};

// useUpdate kept manual — consumers call with { serverId, params }, not { id, params }
export function useUpdateServer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serverId, params }: { serverId: string; params: UpdateServerParams }) =>
      endpoints.updateServer(serverId, params),
    onSuccess: (_data, { serverId }) => {
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.detail(serverId) });
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.all });
    },
  });
}

export function useTestServerConnection() {
  return useMutation({
    mutationFn: (serverId: string) => endpoints.testServerConnection(serverId),
  });
}

export function useTestConnectionRaw() {
  return useMutation({
    mutationFn: (params: TestConnectionRawParams) => endpoints.testConnectionRaw(params),
  });
}

export function useAttachServerSSH() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ serverId, params }: { serverId: string; params: AttachSSHParams }) =>
      endpoints.attachServerSSH(serverId, params),
    onSuccess: (_data, { serverId }) => {
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.detail(serverId) });
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.all });
    },
  });
}

export function useDetachServerSSH() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serverId: string) => endpoints.detachServerSSH(serverId),
    onSuccess: (_data, serverId) => {
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.detail(serverId) });
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['probe', serverId] });
    },
  });
}

export function useHealthCheckServer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serverId: string) => endpoints.healthCheckServer(serverId),
    onSuccess: (_data, serverId) => {
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.detail(serverId) });
    },
  });
}

export function useExecuteCommand() {
  return useMutation({
    mutationFn: ({ serverId, params }: { serverId: string; params: ExecuteCommandParams }) =>
      endpoints.executeCommand(serverId, params),
  });
}

export function useServerHandoff(serverId: string) {
  return useQuery({
    queryKey: serverQueryKeys.handoff(serverId),
    queryFn: () => endpoints.getServerHandoff(serverId),
    enabled: Boolean(serverId),
    staleTime: 60_000,
    gcTime: GC_TIMES.servers,
  });
}

export function useRuntimeInventory(serverId: string) {
  return useQuery({
    queryKey: serverQueryKeys.runtimeInventory(serverId),
    queryFn: () => endpoints.getRuntimeInventory(serverId),
    enabled: Boolean(serverId),
    staleTime: 30_000,
    gcTime: GC_TIMES.servers,
  });
}

// --- Server Groups ---

export function useServerGroups(teamId?: string) {
  return useQuery({
    queryKey: serverQueryKeys.groupList(teamId),
    queryFn: () => endpoints.listServerGroups(teamId ? { teamId } : undefined),
    staleTime: STALE_TIMES.servers,
    gcTime: GC_TIMES.servers,
  });
}

export function useCreateServerGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateServerGroupParams) => endpoints.createServerGroup(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.groups });
    },
  });
}

export function useUpdateServerGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, params }: { groupId: string; params: UpdateServerGroupParams }) =>
      endpoints.updateServerGroup(groupId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.groups });
    },
  });
}

export function useDeleteServerGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => endpoints.deleteServerGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.groups });
      queryClient.invalidateQueries({ queryKey: serverQueryKeys.all });
    },
  });
}

// --- Tag definitions ---

export function useServerTagDefinitions(teamId?: string, category?: string) {
  return useQuery({
    queryKey: ['server-tag-definitions', teamId, category] as const,
    queryFn: () =>
      endpoints.listServerTagDefinitions({
        ...(teamId ? { teamId } : {}),
        ...(category ? { category } : {}),
      }),
    staleTime: STALE_TIMES.servers,
    gcTime: GC_TIMES.servers,
  });
}

export function useCreateServerTagDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: import('../api/types.ts').CreateServerTagDefinitionParams) =>
      endpoints.createServerTagDefinition(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['server-tag-definitions'] });
    },
  });
}

export function useUpdateServerTagDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tagId,
      params,
    }: {
      tagId: string;
      params: import('../api/types.ts').UpdateServerTagDefinitionParams;
    }) => endpoints.updateServerTagDefinition(tagId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['server-tag-definitions'] });
    },
  });
}

export function useDeleteServerTagDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => endpoints.deleteServerTagDefinition(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['server-tag-definitions'] });
    },
  });
}

// --- Server shares (owner manage) ---

export function useServerShares(serverId: string, includeInactive = false) {
  return useQuery({
    queryKey: ['servers', serverId, 'shares', includeInactive ? 'history' : 'active'] as const,
    queryFn: () => endpoints.listServerShares(serverId, { includeInactive }),
    enabled: !!serverId,
    staleTime: 15_000,
    retry: false, // 403 for non-owner should not spam
  });
}

export function useCreateServerShare(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: import('../api/types.ts').CreateServerShareParams) =>
      endpoints.createServerShare(serverId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'shares'] });
    },
  });
}

export function useRevokeServerShare(serverId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => endpoints.revokeServerShare(serverId, shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'shares'] });
    },
  });
}

export function useResolveUser() {
  return useMutation({
    mutationFn: (q: string) => endpoints.resolveUser(q),
  });
}

export { useServers, useServer, useDeleteServer };
